import { useQueryClient } from "@tanstack/react-query";

import { useInternationalization, useNotification } from "@/lib/contexts";
import API from "../../..";
import { CreateRoomErpIntegrationsError } from "../../../rooms/errors/create-room-erp-integrations-error.errors";
import { useMutationWrapper } from "../../wrappers";
import { createRoomKey, roomListKey } from "../room-query-keys";

interface CreateRoomMutationParams {
  onSuccess?: (roomId: string) => void;
  isCustomerOrManagerUser: boolean;
}

export function useMutationCreateRoom({
  onSuccess,
  isCustomerOrManagerUser,
}: CreateRoomMutationParams) {
  const { t } = useInternationalization("pages.rooms.alerts.create-room");
  const { success, error } = useNotification();

  const queryClient = useQueryClient();

  const invalidateQueryKeys = () => {
    queryClient.invalidateQueries({ queryKey: roomListKey() });
    // Invalidate used licenses cache to ensure accurate license availability
    queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey;
        return (
          Array.isArray(queryKey) && queryKey[0] === "rooms" && queryKey[1] === "room-used-licenses"
        );
      },
    });
  };

  const handleSuccess = async (roomId: string) => {
    success(t("success"));
    invalidateQueryKeys();
    if (onSuccess) {
      onSuccess(roomId);
    }
  };

  const handleError = async (err: Error) => {
    const isErpIntegrationsError = err instanceof CreateRoomErpIntegrationsError;

    if (!isCustomerOrManagerUser) {
      if (isErpIntegrationsError) {
        error(t("erp-integrations-error"));
      } else {
        error(t("error"));
      }
    } else {
      if (isErpIntegrationsError) {
        success(t("success"));
      } else {
        error(t("error"));
      }
    }
  };

  const { mutate, isPending, isSuccess, isError } = useMutationWrapper({
    mutationKey: createRoomKey(),
    mutationFn: API.Rooms.createRoom,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  return {
    mutate,
    isPending,
    isSuccess,
    isError,
  };
}
